/**
 * SubmissionsV2 - Lab Submissions with modern design
 * Features:
 * - Summary statistics cards (Total Students, Active Students, Programming, Virtual, Other)
 * - Search bar with debounce
 * - View dropdown for column toggling
 * - Actions dropdown (Download Selected, Download CSV)
 * - Dynamic activity columns based on activities from API
 * - Checkbox selection for bulk download
 * - Server-side pagination and sorting
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumb, Dropdown, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDebounce } from 'use-debounce';
import * as constants from 'Constants';
import httpService from 'services/httpService';
import fileDownload from 'js-file-download';
import displayGlobalMessage from 'Utils/DisplayGlobalMessage';
import { loadCoursesListRequest } from 'Screens_old/PublicCourses/Common/Actions/Actions';
import { loadCoursesListRequest as instructor_loadCoursesListRequest } from 'Store/MyDashboard/Instructor/Actions';
import { toast } from 'react-toastify';
import log from 'loglevel';

// Icons
import { FiUsers, FiSearch, FiChevronDown, FiChevronUp, FiDownload, FiFilter, FiChevronsUp } from 'react-icons/fi';
import { BsClipboardCheck } from 'react-icons/bs';
import { HiOutlineCode } from 'react-icons/hi';
import { MdOutlineMonitor, MdOutlineQuiz } from 'react-icons/md';

import styles from './SubmissionsV2.module.css';

const PAGE_SIZE = 100;

// Column display names for activity types
const PROGRAMMING_COLUMN_DISPLAY_NAMES = {
  evaluationmarks: "Evaluation Marks",
  manualmarks: "Manual Marks",
  rubricmarks: "Rubric Marks",
  totalmarks: "Total Marks"
};

const VIRTUAL_COLUMN_DISPLAY_NAMES = {
  evaluationmarks: "Evaluation Marks",
  manualmarks: "Manual Marks",
  totalmarks: "Total Marks"
};

const OTHER_COLUMN_DISPLAY_NAMES = {
  evaluationmarks: "Evaluation Marks",
  manualmarks: "Manual Marks",
  totalmarks: "Total Marks"
};

// Get columns for activity type
const getActivityColumns = (activityType) => {
  if (activityType === "virtual") {
    return ['evaluationmarks', 'manualmarks'];
  } else if (activityType === "programming") {
    return ['evaluationmarks', 'manualmarks', 'rubricmarks'];
  } else if (activityType === "other") {
    return ['evaluationmarks', 'manualmarks'];
  }
  return [];
};

// Get display name for column type
const getColumnDisplayName = (activityType, columnType) => {
  if (activityType === 'virtual') {
    return VIRTUAL_COLUMN_DISPLAY_NAMES[columnType] || columnType;
  } else if (activityType === 'programming') {
    return PROGRAMMING_COLUMN_DISPLAY_NAMES[columnType] || columnType;
  } else if (activityType === 'other') {
    return OTHER_COLUMN_DISPLAY_NAMES[columnType] || columnType;
  }
  return columnType;
};

const StatCard = ({ label, value, icon: Icon, colorClass }) => (
  <div className={styles.statCard}>
    <div className={styles.statCardContent}>
      <span className={styles.statCardLabel}>{label}</span>
      <span className={`${styles.statCardValue} ${styles[colorClass]}`}>{value}</span>
    </div>
    <div className={`${styles.statCardIcon} ${styles[colorClass + 'Bg']}`}>
      <Icon size={20} />
    </div>
  </div>
);

const SubmissionsV2 = () => {
  const { courseId, labId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const navEl = document.querySelector('.sticky-top');
    const navH = navEl ? navEl.getBoundingClientRect().height : 60;
    document.documentElement.style.setProperty('--bt-navbar-h', `${navH}px`);
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Extract labID from URL if not in params
  const urlArray = location.pathname.split('/');
  const labID = labId || urlArray[6];

  // Data state
  const [submissionsSummary, setSubmissionsSummary] = useState({});
  const [submissions, setSubmissions] = useState([]);
  const [activities, setActivities] = useState([]);
  const [labTitle, setLabTitle] = useState('');
  const [deadline, setDeadline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const tableWrapperRef = useRef(null);

  // Selection state
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Search and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    email: true,
    rollNo: true,
    updatedOn: true,
    activitiesSubmitted: true,
    activities: true,
  });

  // Dropdown states
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);

  // Activity filter state
  const [selectedActivity, setSelectedActivity] = useState('all');
  const [showActivityDropdown, setShowActivityDropdown] = useState(false);

  // Course name
  const [courseName, setCourseName] = useState('');
  const instructorCourses = useSelector((state) => state.instructorCoursesMap);
  const courses = useSelector((state) => state.publicCoursesMap);

  // Load course lists
  useEffect(() => {
    dispatch(loadCoursesListRequest());
    dispatch(instructor_loadCoursesListRequest());
  }, [dispatch]);

  // Get course name
  useEffect(() => {
    let tempCourse;
    if (courses?.length > 0) {
      tempCourse = courses.find((item) => item.id === Number(courseId));
      if (tempCourse) setCourseName(tempCourse.title);
    }
    if (!tempCourse && instructorCourses?.length > 0) {
      tempCourse = instructorCourses.find((item) => item.id === Number(courseId));
      if (tempCourse) setCourseName(tempCourse.title);
    }
  }, [courses, instructorCourses, courseId]);

  // Fetch lab title
  useEffect(() => {
    const fetchLabTitle = async () => {
      if (!courseId || !labID) return;
      try {
        const url = `${constants.base_url}/lab/all/${labID}?courseId=${courseId}`;
        const response = await httpService.get(url);
        setLabTitle(response.data.lab.title);
        setDeadline(response.data.lab.available_till);
      } catch (error) {
        log.error('Error fetching lab title:', error);
      }
    };
    fetchLabTitle();
  }, [labID, courseId]);

  // Reset to page 1 when search/sort changes
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setSubmissions([]);
  }, [debouncedSearchQuery, sortField, sortOrder]);

  // Fetch submissions data
  useEffect(() => {
    const fetchSubmissions = async () => {
      const isFirstPage = currentPage === 1;
      if (isFirstPage) {
        if (submissions.length === 0 && loading) {
          setLoading(true);
        } else {
          setIsTableLoading(true);
        }
      } else {
        setIsLoadingMore(true);
      }
      try {
        const sortParam = sortOrder === 'desc' ? '-' : '';
        const url = `${constants.base_url}/evalpro/labsubmissions/${labID}?page=${currentPage}&page_size=${PAGE_SIZE}&search_query=${debouncedSearchQuery}&sort=${sortParam}${sortField}`;
        const response = await httpService.get(url);

        const newResults = response.data.submissions.submissions?.results || [];
        const count = response.data.submissions.submissions?.count || 0;

        if (isFirstPage) {
          setActivities(response.data.submissions.activities || []);
          setSubmissionsSummary(response.data.submissions);
          setSubmissions(newResults);
        } else {
          setSubmissions((prev) => [...prev, ...newResults]);
        }
        setTotalCount(count);
        setHasMore(currentPage * PAGE_SIZE < count);
      } catch (error) {
        toast.error(error.response?.data?.error_msg || 'Error fetching submissions', {
          position: 'top-right',
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
        setIsTableLoading(false);
        setIsLoadingMore(false);
        isLoadingMoreRef.current = false;
      }
    };

    if (labID) {
      fetchSubmissions();
    }
  }, [labID, currentPage, debouncedSearchQuery, sortField, sortOrder]);

  // Handle select all
  const handleSelectAll = useCallback((checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedSubmissions(submissions);
    } else {
      setSelectedSubmissions([]);
    }
  }, [submissions]);

  // Handle row selection
  const handleRowSelect = useCallback((student, checked) => {
    if (checked) {
      setSelectedSubmissions((prev) => [...prev, student]);
    } else {
      setSelectedSubmissions((prev) => prev.filter((s) => s.student !== student.student));
      setSelectAll(false);
    }
  }, []);

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortOrder === '') {
        setSortOrder('desc');
      } else if (sortOrder === 'desc') {
        setSortOrder('');
        setSortField('');
      } else {
        setSortOrder('');
        setSortField('');
      }
    } else {
      setSortField(field);
      setSortOrder('');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FiChevronsUp size={14} className={styles.sortIconDefault} />;
    return sortOrder === 'desc' ? <FiChevronDown size={14} /> : <FiChevronUp size={14} />;
  };

  // Handle infinite scroll
  const handleScroll = useCallback(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper || isLoadingMoreRef.current || !hasMore) return;
    if (wrapper.scrollTop + wrapper.clientHeight >= wrapper.scrollHeight - 100) {
      isLoadingMoreRef.current = true;
      setCurrentPage((prev) => prev + 1);
    }
  }, [hasMore]);

  // Download selected submissions as ZIP
  const downloadSelectedSubmissions = async () => {
    // Check if there are any submissions
    if (!submissionsSummary.total_submissions || submissionsSummary.total_submissions === 0) {
      displayGlobalMessage('No submissions to download', 'error');
      return;
    }

    if (selectedSubmissions.length === 0) {
      displayGlobalMessage('Select at least one student!', 'error');
      return;
    }

    try {
      let url = `${constants.base_url}/evalpro/labsubmissions/download_zip/${labID}`;
      let requestData = {};

      if (selectAll) {
        url += '?select_all=true';
      } else {
        const allEmpty = selectedSubmissions.every(
          (submission) => Object.keys(submission.activities || {}).length === 0
        );
        if (allEmpty) {
          displayGlobalMessage("All selected students haven't submitted any activity!", 'error');
          return;
        }
        const student_ids = selectedSubmissions.map((submission) => submission.student);
        requestData = { student_ids };
      }

      const response = await httpService.instance({
        url,
        method: 'POST',
        data: requestData,
        responseType: 'blob',
      });

      fileDownload(response.data, `Submissions_${labID}.zip`);
      displayGlobalMessage('Submissions Exported Successfully!', 'success');
    } catch (error) {
      log.error(error);
      displayGlobalMessage('Error occurred while downloading!', 'error');
    }
  };

  // Download as CSV
  const downloadCSV = async () => {
    // Check if there are any submissions
    if (!submissionsSummary.total_submissions || submissionsSummary.total_submissions === 0) {
      displayGlobalMessage('No submissions to download', 'error');
      return;
    }

    try {
      let url = `${constants.base_url}/evalpro/labsubmissions/download_csv/${labID}`;
      if (selectedActivity !== 'all') {
        const [activityType, activityId] = selectedActivity.split('-');
        url += `?activity_type=${activityType}&activity_id=${activityId}`;
      }
      const response = await httpService.get(url, { responseType: 'blob' });
      fileDownload(response.data, 'submissions.csv');
      displayGlobalMessage('CSV downloaded successfully!', 'success');
    } catch (error) {
      log.error(error);
      displayGlobalMessage('Error downloading CSV', 'error');
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Not Submitted';
    const date = new Date(timestamp);
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };
    return new Intl.DateTimeFormat('en-GB', options).format(date);
  };

  // Check if submission is late
  const isLate = (timestamp) => {
    if (!timestamp || !deadline) return false;
    return new Date(timestamp) > new Date(deadline);
  };

  // Toggle column visibility
  const toggleColumn = (column) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingWrapper}>
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <Breadcrumb className={styles.breadcrumb}>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}` }}>
          {courseName || 'Course'}
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs` }}>
          Labs
        </Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/courseware/course/${courseId}/content/labs/${labID}/` }}>
          {labTitle || 'Lab'}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Submissions</Breadcrumb.Item>
      </Breadcrumb>

      {/* Statistics Cards */}
      <div className={styles.statsRow}>
        <StatCard
          label="TOTAL STUDENTS"
          value={submissionsSummary.total_students || 0}
          icon={FiUsers}
          colorClass="blue"
        />
        <StatCard
          label="ACTIVE STUDENTS"
          value={submissionsSummary.total_submissions || 0}
          icon={BsClipboardCheck}
          colorClass="green"
        />
        <StatCard
          label="PROGRAMMING ACTIVITY"
          value={submissionsSummary.programming_activities || 0}
          icon={HiOutlineCode}
          colorClass="cyan"
        />
        <StatCard
          label="VIRTUAL ACTIVITY"
          value={submissionsSummary.virtual_activities || 0}
          icon={MdOutlineMonitor}
          colorClass="purple"
        />
        <StatCard
          label="QUIZ ACTIVITY"
          value={submissionsSummary.other_labs || 0}
          icon={MdOutlineQuiz}
          colorClass="pink"
        />
      </div>

      {/* Search and Actions Bar */}
      <div className={styles.tableContainer}>
        <div className={styles.searchActionsBar}>
          <div className={styles.searchBar}>
            <FiSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.actionButtons}>
            {/* Activity Filter Dropdown */}
            <Dropdown
              show={showActivityDropdown}
              onToggle={(isOpen) => setShowActivityDropdown(isOpen)}
            >
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="activity-filter-tooltip">
                    {selectedActivity === 'all'
                      ? 'All Activities'
                      : activities.find(a => `${a[1]}-${a[2]}` === selectedActivity)?.[0] || 'All Activities'
                    }
                  </Tooltip>
                }
              >
                <Dropdown.Toggle variant="outline-secondary" className={styles.activityFilterButton}>
                  <span className={styles.activityFilterButtonText}>
                    {selectedActivity === 'all'
                      ? 'All Activities'
                      : activities.find(a => `${a[1]}-${a[2]}` === selectedActivity)?.[0] || 'All Activities'
                    }
                  </span>
                  <FiChevronDown />
                </Dropdown.Toggle>
              </OverlayTrigger>
              <Dropdown.Menu className={styles.activityFilterMenu}>
                <Dropdown.Item
                  onClick={() => setSelectedActivity('all')}
                  active={selectedActivity === 'all'}
                >
                  All Activities
                </Dropdown.Item>
                <Dropdown.Divider />
                {activities.map((activity) => {
                  const [activityName, activityType, activityId] = activity;
                  const value = `${activityType}-${activityId}`;
                  return (
                    <Dropdown.Item
                      key={value}
                      onClick={() => setSelectedActivity(value)}
                      active={selectedActivity === value}
                    >
                      {activityName} ({activityType})
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>

            {/* View Dropdown */}
            <Dropdown
              show={showViewDropdown}
              onToggle={(isOpen) => setShowViewDropdown(isOpen)}
            >
              <Dropdown.Toggle variant="primary" className={styles.viewButton}>
                View <FiChevronDown />
              </Dropdown.Toggle>
              <Dropdown.Menu className={styles.viewDropdownMenu}>
                <div className={styles.dropdownHeader}>TOGGLE COLUMNS</div>
                {[
                  { key: 'name', label: 'Name' },
                  { key: 'email', label: 'Email' },
                  { key: 'rollNo', label: 'Roll No' },
                  { key: 'updatedOn', label: 'Updated On' },
                  { key: 'activitiesSubmitted', label: 'Activities Submitted' },
                  { key: 'activities', label: 'Activities' },
                ].map((col) => (
                  <Dropdown.Item
                    key={col.key}
                    as="div"
                    className={styles.columnToggleItem}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={visibleColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                      />
                      <span>{col.label}</span>
                    </label>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            {/* Actions Dropdown */}
            <Dropdown
              show={showActionsDropdown}
              onToggle={(isOpen) => setShowActionsDropdown(isOpen)}
            >
              <Dropdown.Toggle variant="primary" className={styles.actionsButton}>
                Actions <FiChevronDown />
              </Dropdown.Toggle>
              <Dropdown.Menu align="end" className={styles.actionsDropdownMenu}>
                <Dropdown.Item onClick={downloadSelectedSubmissions}>
                  <FiDownload className={styles.dropdownIcon} />
                  Download Selected Submissions
                </Dropdown.Item>
                <Dropdown.Item onClick={downloadCSV}>
                  <FiDownload className={styles.dropdownIcon} />
                  Download as CSV
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* Submissions Table */}
        <div className={styles.tableWrapper} ref={tableWrapperRef} onScroll={handleScroll}>
          {isTableLoading && (
            <div className={styles.tableLoadingOverlay}>
              <Spinner animation="border" size="sm" />
            </div>
          )}
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.checkboxCol}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th className={styles.snoCol}>S.NO.</th>
                {visibleColumns.email && <th className={`${styles.emailCol} ${styles.sortable}`} onClick={() => handleSort('email')}>EMAIL {getSortIcon('email')}</th>}
                {visibleColumns.name && <th className={`${styles.nameCol} ${styles.sortable}`} onClick={() => handleSort('first_name')}>NAME {getSortIcon('first_name')}</th>}
                {visibleColumns.rollNo && <th className={`${styles.rollCol} ${styles.sortable}`} onClick={() => handleSort('roll_no')}>ROLL NO {getSortIcon('roll_no')}</th>}
                {visibleColumns.updatedOn && <th className={`${styles.updatedCol} ${styles.sortable}`} onClick={() => handleSort('latestupload')}>UPDATED ON {getSortIcon('latestupload')}</th>}
                {visibleColumns.activitiesSubmitted && (
                  <th className={`${styles.activitiesCountCol} ${styles.sortable}`} onClick={() => handleSort('activitiesdone')}>ACTIVITIES SUBMITTED {getSortIcon('activitiesdone')}</th>
                )}
                {visibleColumns.activities &&
                  activities
                    .filter((activity) => {
                      if (selectedActivity === 'all') return true;
                      const [, activityType, activityId] = activity;
                      return `${activityType}-${activityId}` === selectedActivity;
                    })
                    .flatMap((activity, actIndex) => {
                      // activity = [activity_name, activity_type, activity_id]
                      const activityName = activity[0];
                      const activityType = activity[1];
                      const activityId = activity[2];
                      const columns = getActivityColumns(activityType);

                      return columns.map((colType) => (
                        <th key={`${actIndex}-${colType}`} className={styles.activityCol}>
                          <Link
                            to={`/courseware/course/${courseId}/content/labs/${labID}/${activityType === 'other' ? 'activities/other' : activityType}/${activityId}/submissions`}
                            className={styles.activityHeaderLink}
                          >
                            <div>{activityType}</div>
                            <div>{activityName}</div>
                            <div>{getColumnDisplayName(activityType, colType)}</div>
                          </Link>
                        </th>
                      ));
                    })}
                {visibleColumns.activities && activities.length === 0 && (
                  <th className={styles.activityCol}>LAB ACTIVITIES(0)</th>
                )}
              </tr>
            </thead>
            <tbody>
              {submissions.length === 0 && !loading && !isTableLoading ? (
                <tr>
                  <td colSpan={10} className={styles.emptyState}>
                    No submissions found
                  </td>
                </tr>
              ) : (
                submissions.map((student, index) => {
                  const isSelected = selectedSubmissions.some((s) => s.student === student.student);
                  return (
                    <tr key={student.student || index} className={styles.tableRow}>
                      <td className={styles.checkboxCell}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleRowSelect(student, e.target.checked)}
                        />
                      </td>
                      <td className={styles.snoCell}>{index + 1}</td>
                      {visibleColumns.email && (
                        <td className={styles.emailCell}>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id={`email-tooltip-${student.student}`}>{student.email}</Tooltip>}
                          >
                            <span className={styles.emailText}>{student.email}</span>
                          </OverlayTrigger>
                        </td>
                      )}
                      {visibleColumns.name && (
                        <td className={styles.nameCell}>{student.first_name}</td>
                      )}
                      {visibleColumns.rollNo && (
                        <td className={styles.rollCell}>{student.roll_no || '-'}</td>
                      )}
                      {visibleColumns.updatedOn && (
                        <td
                          className={`${styles.updatedCell} ${
                            isLate(student.latestupload) ? styles.lateSubmission : ''
                          }`}
                        >
                          {formatTimestamp(student.latestupload)}
                        </td>
                      )}
                      {visibleColumns.activitiesSubmitted && (
                        <td className={styles.activitiesCountCell}>
                          {student.activitiesdone || 0}
                        </td>
                      )}
                      {visibleColumns.activities &&
                        (activities.length > 0 ? (
                          activities
                            .filter((activity) => {
                              if (selectedActivity === 'all') return true;
                              const [, activityType, activityId] = activity;
                              return `${activityType}-${activityId}` === selectedActivity;
                            })
                            .flatMap((activity, actIndex) => {
                              const activityName = activity[0];
                              const activityType = activity[1];
                              const columns = getActivityColumns(activityType);

                              return columns.map((colType) => {
                                const value = student.activities?.[activityName]?.[colType];
                                const uploadTimestamp = student.activities?.[activityName]?.uploadtimestamp;

                                return (
                                  <td key={`${actIndex}-${colType}`} className={styles.activityCell}>
                                    {value !== undefined && value !== null ? (
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={
                                          <Tooltip id={`tooltip-${student.student}-${actIndex}-${colType}`}>
                                            Upload Timestamp: {uploadTimestamp ? formatTimestamp(uploadTimestamp) : 'N/A'}
                                          </Tooltip>
                                        }
                                      >
                                        <span>{value}</span>
                                      </OverlayTrigger>
                                    ) : (
                                      'Not Attempted'
                                    )}
                                  </td>
                                );
                              });
                            })
                        ) : (
                          <td className={styles.activityCell}>0</td>
                        ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {isLoadingMore && (
            <div className={styles.loadingMoreRow}>
              <Spinner animation="border" size="sm" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubmissionsV2;
